import { BaseClient } from '../BaseClient';
import type { Empty } from '../compiled-protos/common';
import type {
  AuthorizeCardsReq,
  CancelCardOnFileLinkReq,
  CancelTerminalCaptureReq,
  CardLinkStatus,
  CardOnFileLinkResp,
  CardReaderList,
  ChargeCardOnFileReq,
  ChargeCardOnFileResp,
  CheckoutReq,
  CheckoutResp,
  CompleteCardSaveReq,
  CreateCardOnFileLinkReq,
  GetCardLinkStatusReq,
  GetHelcimAddonReq,
  GetHelcimPlanReq,
  HelcimAddon,
  HelcimCardAudit,
  HelcimCardAuditList,
  HelcimCardList,
  HelcimEvent,
  HelcimEventList,
  HelcimPlan,
  HelcimProfileList,
  LinkHelcimProfileReq,
  ListCardReadersReq,
  ListCardsOnFileReq,
  LookupPrepaidTransactionReq,
  PrepaidTransactionLookup,
  PublicCardActionReq,
  PublicCardLinkData,
  PublicCardLinkReq,
  RemoveHelcimCardReq,
  ResendCardOnFileLinkReq,
  SearchHelcimProfilesReq,
  SetPreferredHelcimCardReq,
  StartTerminalCaptureReq,
  StartTerminalCaptureResp,
  UnlinkHelcimProfileReq,
  VerifyCardCheckoutReq,
} from '../compiled-protos/helcim';
import { HelcimServiceClient } from '../compiled-protos/helcim.client';

/**
 * HelcimClient provides a wrapper around the HelcimServiceClient for payment processing.
 * Used for creating checkout sessions and managing Helcim payment events.
 */
export class HelcimClient extends BaseClient {
  private helcimService: HelcimServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.helcimService = new HelcimServiceClient(this.transport);
  }

  /**
   * Creates a Helcim checkout session for payment processing.
   * Returns a checkout token that can be used with Helcim's hosted checkout.
   */
  public async createCheckoutSession(req: CheckoutReq): Promise<CheckoutResp | undefined> {
    try {
      const res = await this.helcimService.createCheckoutSession(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to create Helcim checkout session:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Creates a Helcim checkout session that allows partial payments.
   */
  public async createCheckoutPartialPayment(req: CheckoutReq): Promise<CheckoutResp | undefined> {
    try {
      const res = await this.helcimService.createCheckoutPartialPayment(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to create Helcim partial payment checkout:', err);
      return undefined;
    }
  }

  /**
   * Helcim Pay verify / tokenize-only checkout (no invoice). Prefer for card-on-file capture when
   * there is no Kalos invoice yet.
   */
  public async createVerifyCardCheckoutSession(
    req: VerifyCardCheckoutReq,
  ): Promise<CheckoutResp | undefined> {
    try {
      const res = await this.helcimService.createVerifyCardCheckoutSession(req, this.getMetaData())
        .response;
      return res;
    } catch (err) {
      console.error('Failed to create Helcim verify-card checkout session:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Lists saved cards on file for a Kalos user (Helcim customer).
   */
  public async listCardsOnFile(req: ListCardsOnFileReq): Promise<HelcimCardList | undefined> {
    try {
      const res = await this.helcimService.listCardsOnFile(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to list cards on file:', err);
      return undefined;
    }
  }

  /**
   * Stores the user's preferred card token so it can be surfaced as "Current" in the UI.
   */
  public async setPreferredHelcimCard(req: SetPreferredHelcimCardReq): Promise<void> {
    try {
      await this.helcimService.setPreferredHelcimCard(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to set preferred Helcim card:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Emails the customer their card page link (reuses the open link if one exists).
   */
  public async createCardOnFileLink(req: CreateCardOnFileLinkReq): Promise<CardOnFileLinkResp> {
    try {
      return await this.helcimService.createCardOnFileLink(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to create card-on-file link:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Reports the user's open card link, if any — feeds the "Link sent" chip state.
   */
  public async getCardLinkStatus(req: GetCardLinkStatusReq): Promise<CardLinkStatus | undefined> {
    try {
      return await this.helcimService.getCardLinkStatus(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to get card link status:', err);
      return undefined;
    }
  }

  /** Re-emails the open card link. */
  public async resendCardOnFileLink(req: ResendCardOnFileLinkReq): Promise<CardOnFileLinkResp> {
    try {
      return await this.helcimService.resendCardOnFileLink(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to resend card-on-file link:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /** Cancels the open card link; the page stops resolving. */
  public async cancelCardOnFileLink(req: CancelCardOnFileLinkReq): Promise<void> {
    try {
      await this.helcimService.cancelCardOnFileLink(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to cancel card-on-file link:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Records staff-captured consent for saved cards. basis 2 (confirmation email — the
   * email is the record, sent by the server first) or 3 (recorded line, no email).
   */
  public async authorizeCards(req: AuthorizeCardsReq): Promise<HelcimCardList> {
    try {
      return await this.helcimService.authorizeCards(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to authorize cards:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /** Public card page: renders the customer's live card state from a link token. */
  public async getPublicCardLinkData(req: PublicCardLinkReq): Promise<PublicCardLinkData> {
    try {
      return await this.helcimService.getPublicCardLinkData(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to load card link page:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /** Public card page: records a completed card save and returns fresh page state. */
  public async completePublicCardSave(req: PublicCardLinkReq): Promise<PublicCardLinkData> {
    try {
      return await this.helcimService.completePublicCardSave(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to record card save:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /** Public card page: the customer approves one of their saved cards. */
  public async publicAuthorizeCard(req: PublicCardActionReq): Promise<PublicCardLinkData> {
    try {
      return await this.helcimService.publicAuthorizeCard(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to authorize card:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /** Public card page: the customer removes one of their saved cards. */
  public async publicRemoveCard(req: PublicCardActionReq): Promise<PublicCardLinkData> {
    try {
      return await this.helcimService.publicRemoveCard(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to remove card:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Records a staff enter-card-now save: newest card becomes the preferred token
   * and the card_saved audit entry is written with the acting user.
   */
  public async completeCardSave(req: CompleteCardSaveReq): Promise<HelcimCardList> {
    try {
      return await this.helcimService.completeCardSave(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to record card save:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Releases the user's linked Helcim profile: neutralized customerCode (nothing
   * deleted in Helcim), preferred tokens cleared, authorization rows scrubbed.
   */
  public async unlinkHelcimProfile(req: UnlinkHelcimProfileReq): Promise<void> {
    try {
      await this.helcimService.unlinkHelcimProfile(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to unlink Helcim profile:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /** Reads the append-only card-on-file consent audit trail (filter via message fields). */
  public async batchGetHelcimCardAudit(
    req: HelcimCardAudit,
  ): Promise<HelcimCardAuditList | undefined> {
    try {
      return await this.helcimService.batchGetHelcimCardAudit(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to load card audit entries:', err);
      return undefined;
    }
  }

  /**
   * Creates a HelcimEvent record for tracking payment events.
   */
  public async createHelcimEvent(req: HelcimEvent): Promise<HelcimEvent | undefined> {
    try {
      const res = await this.helcimService.createHelcimEvent(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to create Helcim event:', err);
      return undefined;
    }
  }

  /**
   * Retrieves a HelcimEvent by ID.
   */
  public async getHelcimEvent(req: HelcimEvent): Promise<HelcimEvent | undefined> {
    try {
      const res = await this.helcimService.getHelcimEvent(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to get Helcim event:', err);
      return undefined;
    }
  }

  /**
   * Retrieves multiple HelcimEvents.
   */
  public async batchGetHelcimEvent(req: HelcimEvent): Promise<HelcimEventList | undefined> {
    try {
      const res = await this.helcimService.batchGetHelcimEvent(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to batch get Helcim events:', err);
      return undefined;
    }
  }

  /**
   * Updates a HelcimEvent record.
   */
  public async updateHelcimEvent(req: HelcimEvent): Promise<HelcimEvent | undefined> {
    try {
      const res = await this.helcimService.updateHelcimEvent(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to update Helcim event:', err);
      return undefined;
    }
  }

  /**
   * Deletes a HelcimEvent record.
   */
  public async deleteHelcimEvent(req: HelcimEvent): Promise<void> {
    try {
      await this.helcimService.deleteHelcimEvent(req, this.getMetaData()).response;
    } catch (err) {
      console.error('Failed to delete Helcim event:', err);
    }
  }

  /**
   * Lists Helcim card-reader devices paired with the Kalos merchant account.
   */
  public async listCardReaders(req: ListCardReadersReq): Promise<CardReaderList | undefined> {
    try {
      const res = await this.helcimService.listCardReaders(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to list Helcim card readers:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Sends a capture request to the selected card-reader device. The returned
   * helcim_event_id is the row to poll via batchGetHelcimEvent for status.
   */
  /**
   * Charges a saved card token (card on file) through Helcim's Payment API.
   * Synchronous: the response carries the approved/declined outcome. A thrown
   * error means the outcome is unknown — do not blindly retry; the pending
   * HelcimEvent row is settled by reconciliation.
   */
  public async chargeCardOnFile(
    req: ChargeCardOnFileReq,
  ): Promise<ChargeCardOnFileResp | undefined> {
    try {
      const res = await this.helcimService.chargeCardOnFile(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to charge card on file:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  public async startTerminalCapture(
    req: StartTerminalCaptureReq,
  ): Promise<StartTerminalCaptureResp | undefined> {
    try {
      const res = await this.helcimService.startTerminalCapture(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to start terminal capture:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Cancels an in-flight card-reader capture and marks the HelcimEvent cancelled.
   */
  public async cancelTerminalCapture(req: CancelTerminalCaptureReq): Promise<Empty | undefined> {
    try {
      const res = await this.helcimService.cancelTerminalCapture(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to cancel terminal capture:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Fetches a Helcim Recurring Billing payment plan by id. Used by the catalog
   * admin to verify a plan id against Helcim (existence, status, price, term).
   */
  public async getHelcimPlan(req: GetHelcimPlanReq): Promise<HelcimPlan | undefined> {
    try {
      const res = await this.helcimService.getHelcimPlan(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to get Helcim plan:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Fetches a Helcim Recurring Billing add-on by id. Used by the catalog admin
   * to verify an add-on id against Helcim (existence, price, frequency).
   */
  public async getHelcimAddon(req: GetHelcimAddonReq): Promise<HelcimAddon | undefined> {
    try {
      const res = await this.helcimService.getHelcimAddon(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to get Helcim add-on:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Resolves a Helcim transaction id to the payment itself plus the customer's active
   * subscription, if they have one. Used by prepaid enrollment to confirm the payment and find
   * the subscription Helcim already started for an online-form signup.
   *
   * Throws on a bad id or an unreachable Helcim so the caller can message the difference; a
   * customer with no subscription comes back with `subscription` unset, which is not an error.
   */
  public async lookupPrepaidTransaction(
    req: LookupPrepaidTransactionReq,
  ): Promise<PrepaidTransactionLookup | undefined> {
    try {
      const res = await this.helcimService.lookupPrepaidTransaction(req, this.getMetaData())
        .response;
      return res;
    } catch (err) {
      console.error('Failed to look up prepaid transaction:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Searches Helcim customer profiles (by name/email/phone) so an admin can find and link an
   * existing profile. When the request query is empty the server derives a term from the Kalos user.
   */
  public async searchHelcimProfiles(
    req: SearchHelcimProfilesReq,
  ): Promise<HelcimProfileList | undefined> {
    try {
      const res = await this.helcimService.searchHelcimProfiles(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to search Helcim profiles:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Links an existing Helcim profile to the Kalos user (sets the profile's customerCode to the
   * user_id). Throws if the profile is already linked to a different Kalos customer.
   */
  public async linkHelcimProfile(req: LinkHelcimProfileReq): Promise<HelcimCardList | undefined> {
    try {
      const res = await this.helcimService.linkHelcimProfile(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to link Helcim profile:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Removes a saved card from the user's linked Helcim profile. Returns the refreshed card list.
   */
  public async removeHelcimCard(req: RemoveHelcimCardReq): Promise<HelcimCardList | undefined> {
    try {
      const res = await this.helcimService.removeHelcimCard(req, this.getMetaData()).response;
      return res;
    } catch (err) {
      console.error('Failed to remove Helcim card:', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  }
}

export default HelcimClient;
